import { DatePipe } from "@angular/common";
import { GranularityType } from "@type/granularity.type";

const datePipe = new DatePipe('fr-FR');

/**
 * Définit l'heure de début de journée pour une date donnée
 * @param date Date à modifier
 * @returns Date modifiée avec l'heure de début de journée
 */
export function setTimeToStartOfDay(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
}

/**
 * Définit l'heure de fin de journée pour une date donnée
 * @param date Date à modifier
 * @returns Date modifiée avec l'heure de fin de journée
 */
export function setTimeToEndOfDay(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
}

/**
 * Génère toutes les dates dans une période donnée en fonction de la granularité
 * @param startDate Date de début
 * @param endDate Date de fin
 * @param granularity Granularité pour la génération des dates
 * @returns Tableau de dates générées
 */
export function generateAllDatesInPeriodAccordingToGranularity(startDate: Date, endDate: Date, granularity: GranularityType): Date[] {
    const dates: Date[] = [];
    const current = new Date(getDateForGranularity(startDate, granularity));

    while (current <= endDate) {
        dates.push(new Date(current));

        switch (granularity) {
            case 'DAY':
                current.setDate(current.getDate() + 1);
                break;
            case 'WEEK':
                current.setDate(current.getDate() + 7);
                break;
            case 'MONTH':
                current.setMonth(current.getMonth() + 1);
                break;
            case 'YEAR':
                current.setFullYear(current.getFullYear() + 1);
                break;
        }
    }

    return dates;
}

/**
 * Formate une date en chaîne selon la granularité spécifiée
 * @param date Date à formater
 * @param granularity Granularité pour le formatage
 * @returns Chaîne de caractères représentant la date formatée
 */
export function formatDate(date: Date, granularity: GranularityType): string {
    switch (granularity) {
        case 'WEEK': {
            // Date on monday
            const firstDayOfWeek = new Date(date);
            firstDayOfWeek.setDate(date.getDate() - date.getDay() + 1);
            return datePipe.transform(firstDayOfWeek, 'dd-MM-yyyy') ?? '';
        }
        case 'MONTH':
            return datePipe.transform(date, 'MMMM yyyy') ?? '';
        case 'YEAR':
            return datePipe.transform(date, 'yyyy') ?? '';
        default:
            // DAY
            return datePipe.transform(date, 'dd-MM-yyyy') ?? '';
    }
}

/**
 * Récupère la date correspondant à la granularité spécifiée
 * @param targetDate Date cible
 * @param granularity Granularité pour la récupération de la date
 * @returns Date correspondant à la granularité
 */
export function getDateForGranularity(targetDate: Date, granularity: GranularityType): Date {
    const copiedDate = new Date(targetDate);

    switch (granularity) {
        case 'DAY':
            copiedDate.setHours(0, 0, 0, 0);
            break;
        case 'WEEK': {
            // Monday
            const day = copiedDate.getDay();
            const diff = copiedDate.getDate() - day + (day === 0 ? -6 : 1);
            copiedDate.setDate(diff);
            copiedDate.setHours(0, 0, 0, 0);
            break;
        }
        case 'MONTH': {    
            // Start of month
            copiedDate.setDate(1);
            copiedDate.setHours(0, 0, 0, 0);
            break;
        }
        case 'YEAR':{
            // Start of year
            copiedDate.setMonth(0, 1);
            copiedDate.setHours(0, 0, 0, 0);
            break;
        }
    }

    return copiedDate;
}

/**
 * Vérifie si deux dates appartiennent à la même période selon la granularité
 * @param date1 Première date à comparer
 * @param date2 Deuxième date à comparer
 * @param granularity Granularité pour la comparaison
 * @returns Vrai si les deux dates sont dans la même période, faux sinon
 */
export function isSamePeriod(date1: Date, date2: Date, granularity: GranularityType): boolean {
    switch (granularity) {
        case 'DAY':
            return date1.toDateString() === date2.toDateString();
        case 'WEEK': {
            const getWeekStart = (date: Date) => {
                const d = new Date(date);
                const day = d.getDay();
                const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Lundi = début de semaine
                return new Date(d.setDate(diff)).toDateString();
            };
            return getWeekStart(date1) === getWeekStart(date2);
        }
        case 'MONTH':
            return date1.getFullYear() === date2.getFullYear() &&
                date1.getMonth() === date2.getMonth();
        case 'YEAR':
            return date1.getFullYear() === date2.getFullYear();
        default:
            return false;
    }
}

/**
 * Formate une date en chaîne au format ISO pour les requêtes Elasticsearch
 * @param date Date à formater
 * @returns Chaîne au format ISO (ex: 2025-08-22T00:00:00Z)
 */
export function dateToESFormatString(date: Date): string {
    return datePipe.transform(date, 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\'') ?? '';
}

/**
 * Formate une date en chaîne au format ISO sans tenir compte du fuseau horaire
 * @param date Date à formater
 * @returns Chaîne représentant la date au format ISO
 */
export function formatDateToISOFormatWhithoutZoneTime(date: Date): string {
    const copiedDate = new Date(date);
    copiedDate.setMinutes(copiedDate.getMinutes() - copiedDate.getTimezoneOffset());
    return copiedDate.toISOString();
}